/**
 * IPAWS origination config API service (ADR-139 D4).
 *
 * District-level (organization-level) configuration for originating alerts
 * into FEMA IPAWS-OPEN. One config row per district — a COG (Collaborative
 * Operations Group) belongs to the alerting authority, not to a school.
 *
 * Two invariants inherited from the backend (`IPAWSConfigSerializer`):
 *   - `certificate_secret_name` carries the AWS Secrets Manager secret NAME
 *     (`ipaws/cog/<cogId>`), never certificate or key material — the API
 *     rejects anything containing PEM markers (ADR-098 Tier 3 custody).
 *   - Channel enablement MIRRORS the FEMA COG profile; IPAWS-OPEN enforces
 *     the real authorizations server-side (ADR-139 D4).
 *
 * The `/api/v2/ipaws-config/` endpoints are not yet in the generated OpenAPI
 * schema, so calls fall through to `client.ky` (same convention as the
 * licensing endpoints that route around the placeholder schema).
 */

import { client } from '../lib/http-client';

// IPAWS-OPEN dissemination channels a submission can be flagged for
// (ADR-139 D5). Order matches the backend `IPAWSConfig.CHANNELS` tuple.
export const IPAWS_CHANNELS = ['NWEM', 'EAS', 'WEA', 'PUBLIC'] as const;
export type IpawsChannel = (typeof IPAWS_CHANNELS)[number];

/** Human-readable channel names for the config UI. */
export const IPAWS_CHANNEL_LABELS: Record<IpawsChannel, string> = {
  NWEM: 'NWEM — Non-Weather Emergency Messages',
  EAS: 'EAS — Emergency Alert System (radio/TV)',
  WEA: 'WEA — Wireless Emergency Alerts',
  PUBLIC: 'PUBLIC — IPAWS public alert feed',
};

export interface IpawsConfig {
  id: number;
  public_id: string;
  /** District (organization) id — one config per org (ADR-139 D4). */
  district: number;
  /** Master arm switch; false keeps origination dark while config is prepared. */
  is_enabled: boolean;
  /** FEMA-assigned COG identifier. */
  cog_id: string;
  /** AWS Secrets Manager secret NAME (never key material). */
  certificate_secret_name: string;
  /** Subset of IPAWS_CHANNELS — mirrors the FEMA COG profile. */
  enabled_channels: IpawsChannel[];
  /** Default SAME event code per alert-type name, e.g. { Fire: "FRW" }. */
  default_event_codes: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface IpawsConfigWrite {
  district: number;
  is_enabled: boolean;
  cog_id: string;
  certificate_secret_name: string;
  enabled_channels: IpawsChannel[];
  default_event_codes: Record<string, string>;
}

// DRF pagination envelope (DefaultPagination on the viewset).
interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * List the caller's visible IPAWS configs. Reads are org-scoped server-side,
 * so a non-staff admin sees at most their own org's row(s).
 */
export async function listIpawsConfigs(): Promise<IpawsConfig[]> {
  const response = await client.ky.get('api/v2/ipaws-config/');
  const body = (await response.json()) as Paginated<IpawsConfig> | IpawsConfig[];
  return Array.isArray(body) ? body : body.results;
}

export async function createIpawsConfig(
  payload: IpawsConfigWrite,
): Promise<IpawsConfig> {
  const response = await client.ky.post('api/v2/ipaws-config/', {
    json: payload,
  });
  return (await response.json()) as IpawsConfig;
}

export async function updateIpawsConfig(
  id: number,
  payload: Partial<IpawsConfigWrite>,
): Promise<IpawsConfig> {
  const response = await client.ky.patch(`api/v2/ipaws-config/${id}/`, {
    json: payload,
  });
  return (await response.json()) as IpawsConfig;
}

/**
 * Response body of `POST /api/v2/ipaws-config/test-connection/`
 * (announce testConnection → IPAWS-OPEN getAck, ADR-139 D2).
 *
 * The probe rides the announce EventBridge transport, which is
 * fire-and-forget: a `202` only means the probe was DISPATCHED. The getAck
 * outcome surfaces in the ipaws-announce provider logs — never in this
 * response. Non-2xx outcomes (400 gated / 403 forbidden / 502 publish
 * failure) throw ky's `HTTPError`; the same `{ dispatched, detail }` shape
 * rides in that error's response body.
 */
export interface IpawsTestConnectionResult {
  /** True when the probe event was accepted by the announce transport. */
  dispatched: boolean;
  /** Human-readable dispatch outcome — never the probe (getAck) result. */
  detail: string;
  /** Organization (district) id the probe was dispatched for. */
  district?: number;
  /** Site id carried in the probe event (orchestrator config resolution). */
  school?: number | null;
}

/**
 * Dispatch the IPAWS connectivity probe for a district's config.
 * `district` is optional server-side, but pass it explicitly — staff callers
 * who can see multiple orgs' configs get a 400 asking to disambiguate
 * otherwise.
 */
export async function testIpawsConnection(
  district?: number,
): Promise<IpawsTestConnectionResult> {
  const response = await client.ky.post('api/v2/ipaws-config/test-connection/', {
    json: district != null ? { district } : {},
  });
  return (await response.json()) as IpawsTestConnectionResult;
}

/**
 * One persisted connectivity-probe outcome (backend
 * `IPAWSProbeOutcome`). The ipaws-announce provider reports the actual
 * getAck result back through the internal ipaws-outcome endpoint after a
 * fire-and-forget dispatch; this is the read-back of the latest one.
 */
export interface IpawsProbeOutcome {
  id: number;
  /** District (organization) id the outcome belongs to. */
  district: number;
  /** Dispatch correlation id minted by test-connection; null for probe
   *  sources that don't mint one. */
  probe_id: string | null;
  /** COG id the probe ran against, as reported by the provider. */
  cog_id: string;
  status: 'success' | 'error';
  /** Human-readable outcome summary (getAck failure summary on error). */
  message: string;
  /** When the provider observed the getAck outcome (ISO datetime). */
  probed_at: string;
  created_at: string;
}

/**
 * Response body of `GET /api/v2/ipaws-config/last-probe/`.
 * Both fields are null when no config is visible (fail-dark, mirroring
 * `channels`); `last_probe` alone is null when a config exists but no
 * outcome has been reported yet (never probed, or a probe is in flight).
 */
export interface IpawsLastProbeResult {
  district: number | null;
  last_probe: IpawsProbeOutcome | null;
}

/**
 * Read the latest persisted probe outcome for a district's config.
 * Same view surface as the config list (`view_ipawsconfig`,
 * org-scoped); pass `district` explicitly — staff callers who can see
 * multiple orgs' configs get a 400 asking to disambiguate otherwise.
 */
export async function getIpawsLastProbe(
  district?: number,
): Promise<IpawsLastProbeResult> {
  const response = await client.ky.get('api/v2/ipaws-config/last-probe/', {
    searchParams: district != null ? { district } : {},
  });
  return (await response.json()) as IpawsLastProbeResult;
}
